import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CustomAction, NavBar, NavBarTabs, TabLink, UserActions, } from '@guildeducationinc/recess';
import LogoutIcon from '@guildeducationinc/recess/icons/LogoutIcon';
import { ContainerContext } from '@src/contexts';
import { EmployeeContext } from '@src/contexts/EmployeeContextProvider';
import { useApprovals } from '@src/hooks/useApprovals';
import { useAuth } from '@src/hooks/useAuth';
import { usePrograms } from '@src/hooks/usePrograms';
import classNames from 'classnames';
import { TrackableButton, useLocale } from 'pe-core-frontend';
import { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { trackClickEvent } from '../../../../../pe-core-frontend/src/lib/utilities/analytics';
import { ClientSwitcher } from '../ClientSwitcher';
import { LanguageDropdown } from '../LanguageDropdown';
import styles from './HeaderBar.module.scss';
export const HeaderBar = () => {
    const { showHeader } = useContext(ContainerContext);
    const location = useLocation();
    const { t } = useLocale();
    const { currentUser, logout, isClaimsProcessor, isSuperuser, isManager, isMemberSupport, isEmployee, } = useAuth();
    const navigate = useNavigate();
    const { isEligible } = useContext(EmployeeContext);
    const { newRequestUrl } = usePrograms();
    const { total: pendingApprovalCount } = useApprovals({
        clientId: isManager ? currentUser?.clientId : undefined, // skip if not a manager
        onlyPending: true,
        useFilter: false, // Don't apply filter state for the header badge count
    });
    /**
     * Helper to render icon + text content for TabLink.
     * TabLink's children prop is typed as `string`, but the component actually
     * accepts ReactNode at runtime. We use a type assertion to work around
     * the overly restrictive type definition.
     */
    const signOutContent = (_jsxs("span", { className: styles.signOutContent, children: [_jsx(LogoutIcon, {}), " ", t('Sign Out')] }));
    const navLinks = [];
    if (!isSuperuser && !!currentUser && isEmployee) {
        navLinks.push({
            label: 'Home',
            href: '/',
            current: location.pathname === '/',
        });
    }
    const closeNav = (label) => {
        const el = document.querySelector("[data-testid='mobileMenuClose']");
        if (el) {
            trackClickEvent(label, 'header_navigation', 'button', el.textContent ?? '');
            el.click();
        }
    };
    const handleNavClose = (label, callback) => () => {
        closeNav(label);
        setTimeout(callback, 0);
    };
    const actions = [
        {
            type: 'customLink',
            name: t('Profile'),
            Component: Link,
            onClick: handleNavClose('Profile', () => navigate('/profile')),
            appearance: 'text',
        },
        {
            type: 'customLink',
            name: t('Sign Out'),
            Component: Link,
            onClick: handleNavClose('Sign Out', logout),
            appearance: 'outline',
        },
    ];
    if (currentUser && currentUser.employeeId) {
        // All employees can see their own activity and the about benefit page
        navLinks.push({
            label: 'Activity',
            href: '/activity',
            current: location.pathname.startsWith('/activity') ||
                location.pathname.startsWith('/programs') ||
                location.pathname.startsWith('/payments'),
        });
        navLinks.push({
            label: 'About benefit',
            href: '/reimbursement-policy',
            current: location.pathname.startsWith('/reimbursement-policy'),
        });
    }
    if (isManager) {
        // if a user has permission to list users, they can see the employees page
        navLinks.push({
            label: 'Approval',
            href: '/tasks',
            current: location.pathname.startsWith('/tasks'),
            count: pendingApprovalCount > 0 ? pendingApprovalCount : undefined,
            notification: pendingApprovalCount > 0 ? 'danger' : undefined,
        });
    }
    if (isClaimsProcessor || isMemberSupport) {
        navLinks.push({
            label: 'Claims',
            href: '/admin/claims',
            current: location.pathname.startsWith('/admin/claims'),
        });
    }
    if (!showHeader)
        return null;
    return (_jsx("div", { className: classNames('header-bar', {
            'hide-menu': !navLinks?.length,
        }), children: _jsxs(NavBar, { children: [_jsx(ClientSwitcher, {}), _jsxs(NavBarTabs, { children: [navLinks.map(link => (_jsx(TabLink, { current: link.current, Component: Link, to: link.href, onClick: () => closeNav(link.label), count: link.count, notification: link.notification, children: t(link.label) }, link.href))), currentUser && (_jsx(TabLink, { current: false, onClick: handleNavClose('Sign Out', logout), className: styles.mobileOnlyLink, children: signOutContent }, 'sign-out'))] }), isEligible && (_jsx(CustomAction, { showOnMobile: true, children: _jsx("div", { className: styles.headerActions, children: _jsx(TrackableButton, { enText: 'New request', tag: 'header_cta', appearance: 'outline', Component: Link, to: newRequestUrl, children: t('New request') }) }) })), _jsx(CustomAction, { showOnMobile: true, children: _jsx(LanguageDropdown, { id: 'header-language-dropdown', className: styles.languageDropdown }) }), !isClaimsProcessor && !isMemberSupport && (_jsx(_Fragment, { children: _jsx(UserActions, { loggedIn: !!currentUser, loggedInTitle: currentUser?.firstName, testid: 'user-actions', hidePrimaryAction: true, onSecondaryClick: handleNavClose('Sign In', () => navigate('/login')), secondaryTitle: t('Sign In'), loggedinActions: actions }) }))] }) }));
};
